import React, { Component } from 'react';
import { Row, Col } from 'react-bootstrap';
import { isEmpty, isEqual } from 'lodash';
import utils from '@utils';
import PropTypes from 'prop-types';
import { Image } from '@components/common';

class InvoiceSummary extends Component {
	constructor(props) {
		super(props);

		const { showRateAndQuantityColumns } = this.props;

		this.columnProps = {
			xs : showRateAndQuantityColumns ? 3 : 5,
		};
	}

	shouldComponentUpdate({ invoice }) {
		const lastProps = this.props;

		return !isEqual(invoice, lastProps.invoice);
	}

	renderTotalRow = (row) => {
		const { showRateAndQuantityColumns } = this.props;

		return (
			<Row
				className="padding-top-7 padding-bottom-7"
				key={row.id}
			>
				<If condition={showRateAndQuantityColumns}>
					<Col xs={5} />
				</If>
				<Col
					xs={showRateAndQuantityColumns ? 4 : 7}
					className="font-bold"
				>
					{row.description}
				</Col>
				<Col
					xs={this.columnProps.xs}
					className={`text-right color-gray-shuttle font-bold ${row.valueClassName || ''}`}
				>
					{row.value}
				</Col>
			</Row>
		);
	}

	renderItemRow = (row) => {
		const { showRateAndQuantityColumns, showCoordinatorAvatar } = this.props;

		return (
			<Row
				className="padding-top-7 padding-bottom-7"
				key={row.id}
			>
				<Col
					xs={showRateAndQuantityColumns ? 3 : 7}
					className="flex flex-align-items-center"
				>
					<If condition={showCoordinatorAvatar && row.image}>
						<div className="margin-right-10">
							<Image
								className="height-20 width-20 border-radius-rounded img-cover"
								src={row.image}
								fallbackSrc="avatar"
							/>
						</div>
					</If>
					<div className="color-gray-mine-shaft">
						{row.description}
					</div>
				</Col>
				<If condition={showRateAndQuantityColumns}>
					<Col xs={2}>
						{row.rate}
					</Col>
					<Col xs={4}>
						{row.quantity}
						<If condition={row.clockIn && row.clockOut}>
							<br />
							<span className="font-size-12 color-blue-cloud-burst">
								{row.clockIn}
								{' '}
								-
								{' '}
								{row.clockOut}
							</span>
						</If>
					</Col>
				</If>
				<Col
					xs={this.columnProps.xs}
					className={`text-right color-gray-shuttle ${row.valueClassName || ''}`}
				>
					{row.value}
				</Col>
			</Row>
		);
	}

	render() {
		const {
			invoice: {
				Items,
				TotalOfficerPay,
				CoordinatorFees,
				Coordinator,
				TotalCoordinatorFee,
				JurisdictionFees,
				JurisdictionVehicleFees,
				TotalServiceCharge,
				TotalJurisdictionFees,
				Tax,
				TotalInvoice,
			},
			timeZoneId,
			showHeader,
			showDividers,
			showRateAndQuantityColumns,
			showOnlyTotals,
		} = this.props;

		return (
			<div>
				<If condition={showHeader}>
					<Row>
						<Col
							xs={showRateAndQuantityColumns ? 3 : 7}
							className="font-bold"
						>
							Description
						</Col>
						<If condition={showRateAndQuantityColumns}>
							<Col
								xs={2}
								className="font-bold"
							>
								Rate
							</Col>
							<Col
								xs={4}
								className="font-bold"
							>
								Quantity
							</Col>
						</If>
						<Col
							xs={this.columnProps.xs}
							className="font-bold text-right"
						>
							Total
						</Col>
					</Row>
					<div className="separator separator-solid margin-top-10 margin-bottom-10" />
				</If>

				<If condition={!showOnlyTotals}>
					<For
						each="item"
						of={Items || []}
					>
						{this.renderItemRow({
							id          : `invoice_item_${item.Id}`,
							description : item.ItemName,
							rate        : utils.currencyFormatter(item.Price),
							quantity    : item.Quantity,
							clockIn     : item.ClockIn ? utils.parseUTCDateToTimeZone(item.ClockIn, timeZoneId).format('h:mm A') : '',
							clockOut    : item.ClockOut ? utils.parseUTCDateToTimeZone(item.ClockOut, timeZoneId).format('h:mm A') : '',
							value       : utils.currencyFormatter(item.Total),
						})}
					</For>
				</If>

				<If condition={Items && Items.length > 0}>
					{this.renderTotalRow({
						id          : 'invoice_total_officers_pay',
						description : 'Total Officer Pay',
						value       : utils.currencyFormatter(TotalOfficerPay),
					})}
				</If>

				<If condition={!showOnlyTotals && CoordinatorFees && Coordinator && !isEmpty(CoordinatorFees)}>
					{this.renderItemRow({
						id          : 'invoice_coordinator',
						description : Coordinator.Name,
						image       : Coordinator.ImageURL,
						rate        : utils.currencyFormatter(CoordinatorFees.Price),
						quantity    : CoordinatorFees.Quantity,
						value       : utils.currencyFormatter(CoordinatorFees.Total),
					})}
				</If>

				<If condition={TotalCoordinatorFee && TotalCoordinatorFee > 0}>
					{this.renderTotalRow({
						id          : 'invoice_total_coordinator_pay',
						description : 'Total Coordinators Fee',
						value       : utils.currencyFormatter(TotalCoordinatorFee),
					})}
				</If>

				<If condition={(JurisdictionFees && JurisdictionFees.length > 0) || JurisdictionVehicleFees}>
					{this.renderTotalRow({
						id          : 'invoice_total_jurisdiction_fees',
						description : 'Total Jurisdiction Fees',
						value       : utils.currencyFormatter(TotalJurisdictionFees),
					})}
				</If>

				<If condition={showDividers}>
					<div className="separator separator-solid" />
				</If>

				{this.renderTotalRow({
					id          : 'invoice_rkpay_fees',
					description : 'RKPay Fees',
					value       : utils.currencyFormatter(TotalServiceCharge),
				})}

				{this.renderTotalRow({
					id          : 'invoice_taxes',
					description : 'Taxes',
					value       : utils.currencyFormatter(Tax),
				})}

				<If condition={showDividers}>
					<div className="separator separator-solid" />
				</If>

				{this.renderTotalRow({
					id             : 'invoice_total',
					description    : 'TOTAL',
					value          : utils.currencyFormatter(TotalInvoice),
					valueClassName : 'font-bold color-black',
				})}
			</div>
		);
	}
}

InvoiceSummary.propTypes = {
	invoice                    : PropTypes.object.isRequired,
	timeZoneId                 : PropTypes.string.isRequired,
	showHeader                 : PropTypes.bool,
	showDividers               : PropTypes.bool,
	showCoordinatorAvatar      : PropTypes.bool,
	showRateAndQuantityColumns : PropTypes.bool,
	showOnlyTotals             : PropTypes.bool,
};

InvoiceSummary.defaultProps = {
	showHeader                 : true,
	showDividers               : false,
	showCoordinatorAvatar      : true,
	showRateAndQuantityColumns : true,
	showOnlyTotals             : false,
};

export default InvoiceSummary;
